/**
 * Pannello assistente laterale di una conversazione Instagram/Messenger: lo
 * stesso del pannello WhatsApp (sezioni CRM del Lead/Cliente, note, "Chiedi
 * all'AI", Task commerciale dalla risposta) con la sorgente conversazione
 * 'meta' (conversationSource.ts). Chiuso di default sotto i 1440px, poi vale
 * l'ultima scelta (rightPanelPreference.ts, la stessa di WhatsApp).
 *
 * Non ci sono ancora i suggerimenti automatici (whatsapp-agent-analyze lavora
 * solo sulle chat WhatsApp): al loro posto ci sono le domande pronte.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, ExternalLink, Instagram, Facebook, Sparkles, StickyNote, Link2 } from 'lucide-react';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';
import { useMetaMessagingStore } from '@/lib/stores/metaMessagingStore';
import { useAuth } from '@/lib/auth';
import { iconTip, tip } from '@/components/messages/ChatTooltip';
import { initialPanelCollapsed, readPanelPref, writePanelPref, safeLocalStorage } from '@/components/whatsapp2/chat/rightPanelPreference';
import { useClientPanelData } from '@/components/whatsapp2/chat/useClientPanelData';
import { ClientCrmSections } from '@/components/whatsapp2/chat/ClientCrmSections';
import { ChatLeadControls } from '@/components/whatsapp2/chat/ChatLeadControls';
import { AskAiSection } from '@/components/whatsapp2/chat/AskAiSection';
import { SalesTaskFromSuggestionModal } from '@/components/whatsapp2/chat/SalesTaskFromSuggestionModal';
import { taskTitleFromAnswer, type ConversationSource } from '@/components/whatsapp2/chat/conversationSource';
import type { SalesTaskSource } from '@/components/whatsapp2/chat/salesTaskFromSuggestion';
import { conversationLabel, CHANNEL_LABEL } from './metaConversationHelpers';

const QUICK_PROMPTS = [
  { label: 'Riassumi', question: 'Riassumi in poche righe questa conversazione: cosa chiede il contatto e a che punto siamo.', hint: 'Riepilogo breve della conversazione' },
  { label: 'Proponi una risposta', question: 'Scrivi una risposta breve e cordiale all\'ultimo messaggio del contatto, pronta da inviare su Instagram o Messenger. Rispondi solo con il testo del messaggio, senza firma.', hint: 'Una bozza da rivedere prima di inviarla' },
  { label: 'Cosa fare adesso', question: 'Quali sono i prossimi passi concreti con questo contatto?', hint: 'Prossimi passi suggeriti' },
];

interface Props {
  conversation: MetaConversation;
  linkedClientName?: string | null;
  /** Puo' rispondere: abilita "Usa come bozza" e "In chat". */
  canSend: boolean;
  onOpenNotes: () => void;
  /** false quando il pannello e' aperto da un pulsante esterno (drawer compatto): parte espanso. */
  rememberCollapse?: boolean;
}

export function MetaAssistantPanel({ conversation, linkedClientName, canSend, onOpenNotes, rememberCollapse = true }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const setPendingDraft = useMetaMessagingStore((s) => s.setPendingDraft);
  const [collapsed, setCollapsedState] = useState(() =>
    rememberCollapse
      ? initialPanelCollapsed(typeof window !== 'undefined' ? window.innerWidth : 1920, readPanelPref(safeLocalStorage()))
      : false,
  );
  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    if (rememberCollapse) writePanelPref(safeLocalStorage(), value);
  }, [rememberCollapse]);
  const [taskSource, setTaskSource] = useState<SalesTaskSource | null>(null);

  const clientId = conversation.client_id ?? null;
  const organizationId = conversation.organization_id;
  const crm = useClientPanelData({ clientId, organizationId, userId: user?.id, enabled: !collapsed, conversationKey: conversation.id });
  const { client } = crm;
  const Icon = conversation.channel === 'instagram' ? Instagram : Facebook;
  const label = conversationLabel(conversation, linkedClientName);

  const source: ConversationSource = useMemo(
    () => ({ kind: 'meta', organizationId, conversationId: conversation.id, clientId }),
    [organizationId, conversation.id, clientId],
  );
  const insertText = useCallback((text: string) => setPendingDraft(conversation.id, text), [setPendingDraft, conversation.id]);

  if (collapsed) {
    return (
      <div className="w-11 h-full flex-shrink-0 border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-gray-950 flex flex-col items-center pt-3 gap-2">
        <button onClick={() => setCollapsed(false)} className="group p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 transition-colors" aria-expanded={false} {...iconTip('Apri il pannello assistente')}>
          <ChevronLeft className="w-4 h-4 text-[#6F6E69] dark:text-white/60 group-hover:text-[#37352F] dark:group-hover:text-white transition-colors" />
        </button>
        <div className="w-5 h-px bg-[rgba(55,53,47,0.06)] dark:bg-white/6 my-0.5" />
        {clientId && (
          <div className="p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-900/15" {...iconTip('Conversazione collegata a un Lead/Cliente')}>
            <Building2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
          </div>
        )}
        <div className="p-1.5" {...iconTip('Assistente AI')}>
          <Sparkles className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-[380px] max-w-[45%] h-full flex-shrink-0 border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col overflow-hidden bg-white dark:bg-gray-950">
      <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-[rgba(55,53,47,0.06)] dark:border-white/6 flex-shrink-0">
        <button onClick={() => setCollapsed(true)} className="group p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 transition-colors" aria-expanded={true} {...iconTip('Chiudi il pannello assistente')}>
          <ChevronRight className="w-3.5 h-3.5 text-[#6F6E69] dark:text-white/60 group-hover:text-[#37352F] dark:group-hover:text-white transition-colors" />
        </button>
        <div className="flex-1 min-w-0">
          {clientId && client ? (
            <div className="flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <h3 className="text-[13px] font-semibold text-[#37352F] dark:text-white truncate">{client.business_name}</h3>
              <button onClick={() => navigate(`/clients/${clientId}`)} className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white transition-colors" {...iconTip('Apri la scheda (chiude i messaggi)')}>
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#6F6E69] flex-shrink-0" />
              <h3 className="text-[13px] font-medium text-[#37352F] dark:text-white truncate">Assistente</h3>
            </div>
          )}
          <p className="text-[10px] text-[#6F6E69] truncate mt-0.5 inline-flex items-center gap-1">
            <Icon className="w-2.5 h-2.5" /> {label} · {CHANNEL_LABEL[conversation.channel]}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {clientId ? (
          <>
            {client && <ChatLeadControls client={client} />}
            <ClientCrmSections data={crm} onOpenNotes={onOpenNotes} onInsertText={canSend ? insertText : undefined} textStyle="plain" />
          </>
        ) : (
          <>
            <div className="border-b border-[rgba(55,53,47,0.06)] dark:border-white/6">
              <button onClick={onOpenNotes} className="flex items-center gap-2 w-full px-3.5 py-2 hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] transition-colors" {...tip('Note interne sulla conversazione: il contatto non le vede')}>
                <ChevronRight className="w-3 h-3 text-[#6F6E69] dark:text-white/60" />
                <StickyNote className="w-3.5 h-3.5 text-[#6F6E69] dark:text-white/60" />
                <span className="text-[11px] font-medium text-[#37352F] dark:text-white/90 flex-1 text-left">Note</span>
              </button>
            </div>
            <div className="m-3.5 rounded-lg bg-[rgba(55,53,47,0.024)] dark:bg-white/[0.03] px-3 py-2.5 flex gap-2">
              <Link2 className="w-3.5 h-3.5 text-[#6F6E69] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#37352F] dark:text-white/80 leading-[1.6]">
                Collega la conversazione a un Lead o Cliente con "Collega Lead/Cliente" in alto: qui vedrai offerte, progetti e Task commerciali, e potrai crearne di nuovi.
              </p>
            </div>
          </>
        )}
      </div>

      <AskAiSection
        source={source}
        quickPrompts={QUICK_PROMPTS}
        onUseAsDraft={canSend ? insertText : undefined}
        onCreateSalesTask={clientId ? (answer) => setTaskSource({ title: taskTitleFromAnswer(answer), description: answer }) : undefined}
      />

      {taskSource && (
        <SalesTaskFromSuggestionModal
          request={taskSource}
          clientId={clientId}
          clientName={client?.business_name ?? linkedClientName ?? null}
          organizationId={organizationId}
          projectName={null}
          onClose={() => setTaskSource(null)}
          onCreated={() => void crm.loadActivities()}
        />
      )}
    </div>
  );
}
